import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  title?: string;
}

interface Props<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  full?: boolean;
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
  className,
  full,
}: Props<T>) {
  return (
    <div
      role="radiogroup"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-[9px] bg-[var(--bg-sunken)] p-0.5',
        'shadow-[inset_0_0_0_1px_var(--hairline)]',
        full && 'flex w-full',
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={selected}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center justify-center gap-1 rounded-[7px] font-medium transition-all duration-150',
              size === 'sm' ? 'h-6 px-2 text-[12px]' : 'h-7 px-2.5 text-[12.5px]',
              full && 'flex-1',
              selected
                ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.1),0_0_0_0.5px_var(--hairline)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
